import { IconButton } from '@atlaskit/button/new';
import DropdownMenu from '@atlaskit/dropdown-menu';
import CrossIcon from '@atlaskit/icon/core/cross';
import ShowMoreHorizontalIcon from '@atlaskit/icon/core/show-more-horizontal';
import Lozenge from '@atlaskit/lozenge';
import { ButtonItem, MenuGroup, Section } from '@atlaskit/menu';
import { type ThemeColorModes, useThemeObserver } from '@atlaskit/tokens';
import { useFireCrossFlowAnalyticsEvents } from '@atlassian/surface-analytics';
import { Journeys, Targets, useCrossFlow } from '@atlassiansox/cross-flow-support';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';
import { useCallback, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { JpdRoadmapIcon } from '../../assets/jpd-roadmap';
import { JsmItsmDarkIcon } from '../../assets/jsm-itsm-dark';
import { JsmItsmLightIcon } from '../../assets/jsm-itsm-light';
import { JsmWorkRequestsDarkIcon } from '../../assets/jsm-work-requests-dark';
import { JsmWorkRequestsLightIcon } from '../../assets/jsm-work-requests-light';
import { MenuItemBase } from '../menu-item/menu-item';

type ProductKey = typeof Targets.JIRA_SERVICE_DESK | typeof Targets.JIRA_PRODUCT_DISCOVERY;
interface PlacementRenderProps {
	dismiss: () => void;
}
interface MessageProps {
	productKey: ProductKey;
	entityId: string;
	recommendationSessionId: string;
}

export type Nav4RecommendationProps = MessageProps & PlacementRenderProps;

const itsmLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmItsmLightIcon /> : <JsmItsmDarkIcon />;
};

const workRequestLogoByTheme = (colorMode: ThemeColorModes = 'light') => {
	return colorMode === 'light' ? <JsmWorkRequestsLightIcon /> : <JsmWorkRequestsDarkIcon />;
};

const useIcon = ({ productKey, entityId }: Pick<MessageProps, 'productKey' | 'entityId'>) => {
	const { colorMode } = useThemeObserver();

	if (productKey === Targets.JIRA_SERVICE_DESK) {
		if (entityId === 'jsm.general-service-management') {
			return workRequestLogoByTheme(colorMode);
		}
		if (entityId === 'jsm.it-service-management-basic') {
			return itsmLogoByTheme(colorMode);
		}
	}

	if (productKey === Targets.JIRA_PRODUCT_DISCOVERY) {
		return <JpdRoadmapIcon />;
	}

	return null;
};

const useTemplateName = ({
	productKey,
	entityId,
}: Pick<MessageProps, 'productKey' | 'entityId'>) => {
	if (productKey === Targets.JIRA_SERVICE_DESK) {
		if (entityId === 'jsm.general-service-management') {
			return (
				<FormattedMessage
					id="cross-flow-work-requests-template.jsm.title"
					defaultMessage="Collect requests"
					description="Jira Service Management Work Requests template display name"
				/>
			);
		}
		if (entityId === 'jsm.it-service-management-basic') {
			return (
				<FormattedMessage
					id="cross-flow-itsm-template.jsm.title"
					defaultMessage="Collect IT requests"
					description="Jira Service Management ITSM template display name"
				/>
			);
		}
	}

	if (productKey === Targets.JIRA_PRODUCT_DISCOVERY) {
		return (
			<FormattedMessage
				id="cross-flow-product-roadmap-template.jpd.title"
				defaultMessage="Create a roadmap"
				description="Jira Product Discovery product roadmap template display name"
			/>
		);
	}

	return null;
};

export const Nav4Recommendation = ({
	productKey,
	entityId,
	recommendationSessionId,
	dismiss,
}: Nav4RecommendationProps) => {
	const icon = useIcon({ productKey, entityId });
	const templateName = useTemplateName({ productKey, entityId });
	const { formatMessage } = useIntl();
	const crossFlow = useCrossFlow();

	const { fireViewedUIEvent, fireInteractedUIEvent } = useFireCrossFlowAnalyticsEvents();

	const { messageInstanceClicked } = useMessageInstanceEvent();

	useEffect(() => {
		fireViewedUIEvent(
			{
				action: 'viewed',
				actionSubject: 'section',
				actionSubjectId: 'projectsMenu',
			},
			{
				source: 'projectsMenuSection',
				recommendedProductIds: [productKey],
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
			},
			{
				sqa1052Enabled: true,
			},
		);
	}, [fireViewedUIEvent, productKey, recommendationSessionId]);

	const handleClick = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'recommendation',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'react',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
			},
		});

		fireInteractedUIEvent(
			{
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'tryThisRecommendation',
			},
			{
				source: 'projectsMenuSection',
				targetProductId: productKey,
				recommendationSession: {
					id: recommendationSessionId,
					type: 'appRec',
				},
				recommendedProductIds: [productKey],
			},
			{
				sqa1052Enabled: true,
			},
		);

		if (crossFlow.isEnabled) {
			void crossFlow.apiV1.open({
				v1: true,
				sourceComponent: 'projectsMenu',
				sourceContext: 'tryThisRecommendation',
				targetProduct: productKey,
				journey: Journeys.GET_STARTED,
				extensions: {
					provisioningPageOptions: {
						actions: {
							primary: {
								behaviour: 'land',
								prompt: 'next',
								options: {
									targetTab: 'same',
									urlParams: { templateId: entityId },
								},
							},
						},
					},
					recommendationContext: {
						recommendationSessionId,
						entityId,
					},
				},
			});
		}
	}, [
		crossFlow,
		entityId,
		fireInteractedUIEvent,
		messageInstanceClicked,
		productKey,
		recommendationSessionId,
	]);

	const handleDismiss = useCallback(() => {
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'primary',
			},
			action: {
				actionType: 'dismiss',
			},
			additionalMetadataFromFrontend: {
				sqa1052Enabled: true,
			},
		});

		dismiss();
	}, [dismiss, messageInstanceClicked]);

	return (
		<div role="listitem">
			<MenuItemBase
				elemBefore={icon}
				elemAfter={
					<Lozenge appearance="new" isBold={false}>
						<FormattedMessage
							id="post-office.nav-4-projects-recommendations.cta-button-lozenge-try"
							defaultMessage="Try"
							description="A lozenge to display the try button"
						/>
					</Lozenge>
				}
				href=""
				onClick={handleClick}
				actionsOnHover={
					<DropdownMenu
						shouldRenderToParent={true}
						trigger={({ triggerRef, ...props }) => (
							<IconButton
								ref={triggerRef}
								{...props}
								spacing="compact"
								appearance="subtle"
								label={formatMessage({
									id: 'post-office.nav-4-projects-recommendations.dropdown-menu-open-label',
									defaultMessage: 'Open',
									description: 'A label to open the recommendation dropdown menu',
								})}
								icon={(iconProps) => <ShowMoreHorizontalIcon {...iconProps} size="small" />}
							/>
						)}
					>
						<MenuGroup minWidth="250px">
							<Section>
								<ButtonItem onClick={handleDismiss} iconBefore={<CrossIcon label="" />}>
									<FormattedMessage
										id="post-office.nav-4-projects-recommendations.cta-button-dimissal"
										defaultMessage="Dismiss"
										description="A call to action to dismiss the request"
									/>
								</ButtonItem>
							</Section>
						</MenuGroup>
					</DropdownMenu>
				}
			>
				{templateName}
			</MenuItemBase>
		</div>
	);
};
